import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { supabaseClient } from '../../../config/supabase';
import HoverHelp from '../../shared/HoverHelp';
import { useSaveToast } from '../../ui/SaveToast';
import { DEFAULT_EVENT_SETTINGS, settingsSubtitle } from '../eventSettings';
import { DEFAULT_EVENT_WORDS, resolveEventWords } from '../eventWords';
import {
    addEventKit, loadAllEventKits, loadEventSettings, moveEventKit, restoreStarterKits, saveEventSettings, updateEventKit,
} from '../eventSettingsApi';
import GeneralSettingsPopup from './GeneralSettingsPopup';
import GuestsRsvpSettingsPopup from './GuestsRsvpSettingsPopup';
import EventTypesSettingsPopup from './EventTypesSettingsPopup';
import HotelTravelSettingsPopup from './HotelTravelSettingsPopup';
import CheckInSettingsPopup from './CheckInSettingsPopup';
import AutomationPolicyPopup from './AutomationPolicyPopup';

export const EVENTS_SETTINGS_BASE = '/admin/events';
export const EVENTS_ROLES_PATH = '/admin/personnel?tab=access&area=events';

// The tiles. Only areas with something real to configure today; G7 adds its own tile when it has settings (no "coming soon" tiles). G6 added Hotel & Travel, G5 Check-In.
// Q-046: the blurbs use the company's words; the tile names stay the platform's (they match the Admin Center and the help).
export function settingsTiles(words = DEFAULT_EVENT_WORDS) {
    return [
        { key: 'general', icon: '⚙️', title: 'General', blurb: `Default start and end time, ${words.eventLower} type, guests expected, and the words your clients see` },
        { key: 'event-types', icon: '🏷️', title: 'Event Types', blurb: `Your ${words.eventLower} types: add, rename, order, turn on or off, checklists` },
        { key: 'guests-rsvp', icon: '✉️', title: 'Guests & RSVP', blurb: 'Plus-ones, Maybe, the RSVP form and how heads are counted' },
        { key: 'hotel-travel', icon: '🏨', title: 'Hotel & Travel', blurb: `A hotel block on each ${words.eventLower}, and who needs the shuttle` },
        { key: 'check-in', icon: '✅', title: 'Check-In', blurb: 'Checking guests in at the door, QR passes and the door scanner' },
        { key: 'automation', icon: '🤖', title: 'Client Event Automation', blurb: `What clients may switch on when they start an ${words.eventLower}: invitations, reminders, flights and rides` },
        { key: 'roles', icon: '🔐', title: 'Roles & Permissions for Events', blurb: `Who can plan ${words.eventsLower}, view them or manage them`, to: EVENTS_ROLES_PATH },
    ];
}
export const EVENTS_SETTINGS_TILES = settingsTiles();

const HELP = {
    title: 'Events Settings',
    body: 'Set up the Events pack for your organization. These are defaults for every new event, and each event can override them. Only organization admins can change them.',
};

function Tile({ tile }) {
    const body = (
        <>
            <span aria-hidden="true" className="ia-set__tile-icon">{tile.icon}</span>
            <span className="ia-set__tile-text">
                <span className="ia-set__tile-title">{tile.title}</span>
                <span className="ia-set__tile-blurb">{tile.blurb}</span>
            </span>
            <span aria-hidden="true" className="ia-set__tile-chevron">›</span>
        </>
    );
    return (
        <Link to={tile.to || `${EVENTS_SETTINGS_BASE}/${tile.key}`} className="ia-set__tile ia-depth-card" style={{ boxShadow: 'var(--ia-3d-card-depth)' }} data-events-settings-tile={tile.key}>{body}</Link>
    );
}

// Module 19 (BL-380) G8 - Events Settings, inside the Admin Center, Tenant Admins only, and only while the Events pack is on.
// Per the platform settings layout: no left panel, each area is a 3D tile, a tile opens the Create-reservation style popup, one "?" at the top.
// The access check is repeated in the database (the pack's two keys AND a Tenant Admin must both hold for any write).
export default function EventsSettingsView({ userId, orgId, enabled = false, loading = false, isOrgAdmin = false, theme = 'light', client = supabaseClient }) {
    const { tile } = useParams();
    const navigate = useNavigate();
    const toast = useSaveToast();
    const [settings, setSettings] = useState(DEFAULT_EVENT_SETTINGS);
    const [kits, setKits] = useState([]);
    const [ready, setReady] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [loadError, setLoadError] = useState('');
    const [notice, setNotice] = useState('');
    const allowed = enabled && isOrgAdmin;

    const load = useCallback(async () => {
        try {
            const [nextSettings, nextKits] = await Promise.all([loadEventSettings(client), loadAllEventKits(client)]);
            setSettings(nextSettings);
            setKits(nextKits);
            setLoadError('');
        } catch (failure) {
            setLoadError(failure?.message || 'Events Settings could not be loaded.');
        }
        setReady(true);
    }, [client]);

    useEffect(() => { if (allowed) load(); }, [allowed, load]);

    const close = useCallback(() => { setError(''); setNotice(''); navigate(EVENTS_SETTINGS_BASE); }, [navigate]);

    if (loading) return <div className="p-8 text-center text-[var(--ink-muted)]">Loading…</div>;
    if (!allowed) return <Navigate to="/admin" replace />;

    const run = async (work, failureText) => {
        setSaving(true); setError('');
        try { await work(); toast.saved(); return true; } catch (failure) {
            const message = `${failureText}${failure?.message ? ` ${failure.message}` : ''}`.trim();
            setError(message); toast.failed(message); return false;
        } finally { setSaving(false); }
    };

    const saveSettings = async (next) => {
        const ok = await run(async () => { setSettings(await saveEventSettings(client, orgId, userId, next)); }, 'Your settings could not be saved.');
        if (ok) close();
    };
    const changeKit = (kit, patch, failureText = 'That type could not be saved.') => run(async () => {
        const saved = await updateEventKit(client, kit.id, patch);
        setKits((current) => current.map((item) => (item.id === saved.id ? saved : item)));
    }, failureText);

    // A popup opens only after the saved values have loaded, so a deep link can never show blanks and then save them over real settings.
    const canOpenPopup = ready && !loadError;
    const words = resolveEventWords(settings);
    let popup = null;
    if (!canOpenPopup) {
        popup = null;
    } else if (tile === 'general') {
        popup = <GeneralSettingsPopup settings={settings} kits={kits} saving={saving} error={error} onSave={saveSettings} onClose={close} />;
    } else if (tile === 'guests-rsvp') {
        popup = <GuestsRsvpSettingsPopup settings={settings} saving={saving} error={error} onSave={saveSettings} onClose={close} />;
    } else if (tile === 'hotel-travel') {
        popup = <HotelTravelSettingsPopup settings={settings} saving={saving} error={error} onSave={saveSettings} onClose={close} />;
    } else if (tile === 'check-in') {
        popup = <CheckInSettingsPopup settings={settings} saving={saving} error={error} onSave={saveSettings} onClose={close} />;
    } else if (tile === 'automation') {
        popup = <AutomationPolicyPopup client={client} onClose={close} onSaved={() => toast.saved()} />;
    } else if (tile === 'event-types') {
        popup = (
            <EventTypesSettingsPopup
                kits={kits}
                busy={saving}
                error={error}
                notice={notice}
                words={words}
                onClose={close}
                onToggle={(kit, value) => changeKit(kit, { is_active: value })}
                onSaveKit={(kit, values) => changeKit(kit, values)}
                onAdd={(values) => run(async () => { const added = await addEventKit(client, orgId, values, kits); setKits((current) => [...current, added]); }, 'That type could not be added.')}
                onMove={(kit, direction) => run(async () => { setKits(await moveEventKit(client, kits, kit.id, direction)); }, 'The order could not be changed.')}
                onRestore={() => run(async () => {
                    const added = await restoreStarterKits(client);
                    setKits(await loadAllEventKits(client));
                    setNotice(added === 0 ? 'All four starter kits are already here. Nothing was changed.' : `${added} starter ${added === 1 ? 'kit was' : 'kits were'} added back. Your other types were not touched.`);
                }, 'The starter kits could not be restored.')}
            />
        );
    } else if (tile) {
        return <Navigate to={EVENTS_SETTINGS_BASE} replace />;
    }

    return (
        <div data-theme={theme} className="ia-set ia-events-settings" data-testid="events-settings" data-m6-layout="events" data-m6-component="EventsSettings">
            <nav aria-label="Breadcrumb" className="ia-set__crumbs" data-testid="events-settings-breadcrumb">
                <ol>
                    <li className="ia-set__crumb-item"><Link to="/admin" className="ia-btn3d ia-btn3d--pill ia-set__crumb">Admin Center</Link><span aria-hidden="true" className="ia-set__crumb-sep">›</span></li>
                    <li className="ia-set__crumb-item"><span aria-current="page" className="ia-set__crumb-current">Events Settings</span></li>
                </ol>
            </nav>
            <header className="ia-set__head">
                <div className="flex items-center gap-2">
                    <h2 className="ia-set__title">Events Settings</h2>
                    <HoverHelp helpId="events.settings" content={HELP} userId={userId} />
                </div>
                <p className="ia-set__subtitle">{settingsSubtitle(words)}</p>
            </header>
            {loadError && <p role="alert" className="ia-resv-error mb-3">{loadError}</p>}
            {!ready && !loadError ? <p className="text-[var(--ink-muted)]">Loading…</p> : (
                <ul className="ia-set__grid" data-testid="events-settings-grid">
                    {settingsTiles(words).map((item) => <li key={item.key} className="ia-set__cell"><Tile tile={item} /></li>)}
                </ul>
            )}
            {popup}
        </div>
    );
}
